import {
  ARTIST_BIO_LONG_MAX,
  bioLength,
  normalizeBioText,
  spotifyProfileFileRefusal,
  type ArtistProfileKind,
} from './artistProfile';

/**
 * "Send to all" of the artist Profile (Studio → Profile, owner decision 2026-10-20): one action
 * sends the profile images and the bio to every connected service that takes them. The contract
 * of `/accounts/:accountId/artist-profile/send*`, the MCP tools and the jobs. No imports beyond
 * fym-shared: every client may read it.
 *
 * Model: one send (`ProfileSends`) holds one row for each service and item (`ProfileSendItems`).
 * An item goes queued → sending → live | failed (code, message), or it is unavailable (reason) from
 * the start. One job for each service sends its items.
 */

/** The services of the first version. No X: there is no X connector (owner, 2026-10-20). */
export const PROFILE_SEND_SERVICES = ['spotify', 'youtube', 'facebook', 'deezer'] as const;
export type ProfileSendService = (typeof PROFILE_SEND_SERVICES)[number];

/**
 * The parts of a profile. `header` the Spotify header, `picture` the square artist photo, `banner`
 * the YouTube channel banner, `cover` the Facebook cover photo, `bio` the text, `facebookLink` the
 * Facebook page link of the Deezer profile.
 */
export const PROFILE_SEND_ITEMS = ['header', 'picture', 'banner', 'cover', 'bio', 'facebookLink'] as const;
export type ProfileSendItem = (typeof PROFILE_SEND_ITEMS)[number];

/** The items of each service, in the order of the sheet. */
export const PROFILE_SEND_SERVICE_ITEMS: Readonly<Record<ProfileSendService, readonly ProfileSendItem[]>> = {
  spotify: ['header', 'picture', 'bio'],
  youtube: ['banner', 'bio'],
  facebook: ['cover', 'picture', 'bio'],
  deezer: ['picture', 'bio', 'facebookLink'],
};

/** True when the service takes the item. Pure. */
export function serviceTakesItem(service: ProfileSendService, item: ProfileSendItem): boolean {
  return PROFILE_SEND_SERVICE_ITEMS[service].includes(item);
}

/** The image items: each one sends the current version of one profile kind. */
export const PROFILE_SEND_IMAGE_ITEMS: readonly ProfileSendItem[] = ['header', 'picture', 'banner', 'cover'];

export function isImageItem(item: ProfileSendItem): boolean {
  return PROFILE_SEND_IMAGE_ITEMS.includes(item);
}

/** The profile kind that an image item sends. Null for `bio` and `facebookLink`. Pure. */
export function profileKindOfItem(item: ProfileSendItem): ArtistProfileKind | null {
  switch (item) {
    case 'header':
      return 'spotifyHeader';
    case 'picture':
      return 'spotifyAvatar';
    case 'banner':
      return 'youtubeBanner';
    case 'cover':
      return 'facebookCover';
    case 'bio':
    case 'facebookLink':
      return null;
  }
}

/** The service items that send a version of the kind ("Keep my profiles in sync"). Pure. */
export function itemsOfKind(kind: string): Array<{ service: ProfileSendService; item: ProfileSendItem }> {
  const out: Array<{ service: ProfileSendService; item: ProfileSendItem }> = [];
  PROFILE_SEND_SERVICES.forEach((service) => {
    PROFILE_SEND_SERVICE_ITEMS[service].forEach((item) => {
      if (profileKindOfItem(item) === kind) {
        out.push({ service, item });
      }
    });
  });
  return out;
}

// ---------------------------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------------------------

export const PROFILE_SEND_STATUSES = ['queued', 'sending', 'live', 'failed', 'unavailable'] as const;
export type ProfileSendStatus = (typeof PROFILE_SEND_STATUSES)[number];

/** A status that does not change any more. */
export function isFinalSendStatus(status: ProfileSendStatus): boolean {
  return status === 'live' || status === 'failed' || status === 'unavailable';
}

/**
 * Why an item cannot go (`unavailable`):
 * - `notConnected`: the artist did not connect the service.
 * - `reconnect`: the stored sign-in does not work any more.
 * - `needsScope`: the sign-in lacks a permission (YouTube `youtube.force-ssl`, Facebook
 *   `pages_manage_metadata`): reconnect to give it.
 * - `notAvailableYet`: Music24 cannot send it yet (Facebook: the app review of
 *   `pages_manage_metadata` is open, `MUSIC24_FACEBOOK_PROFILE_WRITE` is off).
 * - `noAutoUploadAccess`: the Spotify upload needs the admin switch "Auto upload access".
 * - `nothingReady`: no ready version of the image, or no saved bio.
 * - `preview`: the current file is a half-size preview with the mark (Free and the trial).
 * - `fileRefused`: the current file breaks a rule of the service (`fileRefusal` says which).
 * - `noLink`: no link to send (Deezer: no Facebook page is connected).
 * - `busy`: the same item of the service is on its way in another send.
 */
export const PROFILE_SEND_UNAVAILABLE = [
  'notConnected',
  'reconnect',
  'needsScope',
  'notAvailableYet',
  'noAutoUploadAccess',
  'nothingReady',
  'preview',
  'fileRefused',
  'noLink',
  'busy',
] as const;
export type ProfileSendUnavailable = (typeof PROFILE_SEND_UNAVAILABLE)[number];

/**
 * Why a sent item failed:
 * - `reconnect`: the service refused the stored sign-in.
 * - `noPermission`: the role of the sign-in cannot edit the profile.
 * - `refused`: the service refused the request (`failDetail` has its text).
 * - `fileRefused`, `fileGone`: the file broke a rule at send time, or is gone.
 * - `readOnlyOnService`: the service holds its own text and does not take ours (Deezer biography
 *   with `writable: false`: the artist must press "Replace" on Deezer first).
 * - `unknownAnswer`: the service answered a shape that we do not know (a private API that changed).
 * - `unsure`: a worker stopped after the request went out; the state on the service is not known.
 * - `error`: the service did not answer after the last attempt.
 */
export const PROFILE_SEND_FAIL_CODES = [
  'reconnect',
  'noPermission',
  'refused',
  'fileRefused',
  'fileGone',
  'readOnlyOnService',
  'unknownAnswer',
  'unsure',
  'error',
] as const;
export type ProfileSendFailCode = (typeof PROFILE_SEND_FAIL_CODES)[number];

// ---------------------------------------------------------------------------------------------
// Rights and the sync setting
// ---------------------------------------------------------------------------------------------

/** The line of the sheet, confirmed once per artist (stored with who and when). */
export const PROFILE_SEND_RIGHTS_TEXT =
  'By sending, you agree that you have all of the legal rights to these images, links and the bio, as the terms of each service describe.';
/** Change it when the text changes: a new version asks again. */
export const PROFILE_SEND_RIGHTS_VERSION = '2026-10-20';

// ---------------------------------------------------------------------------------------------
// File and text rules of each service (checked on the stored original file, never on a resized copy)
// ---------------------------------------------------------------------------------------------

const MB = 1024 * 1024;

export interface ProfileSendFileRule {
  minWidth: number;
  minHeight: number;
  maxBytes: number;
  contentTypes: readonly string[];
  /** Width / height that the file must have (± 1 %), or null. */
  aspect: number | null;
}

/**
 * The rules of the image items other than Spotify (Spotify: `spotifyProfileFileRefusal`).
 * - YouTube banner (`channelBanners.insert`): at least 2048 x 1152, 16:9, at most 6 MB, JPEG or PNG.
 * - Facebook cover: at least 400 x 150; picture at least 180 x 180 (Graph Page reference). The
 *   byte limits are our own cap.
 * - Deezer picture: [guess] no limit seen in the client code; at least 500 x 500, square, 10 MB.
 */
export const PROFILE_SEND_FILE_RULES: Readonly<
  Partial<Record<`${ProfileSendService}.${ProfileSendItem}`, ProfileSendFileRule>>
> = {
  'youtube.banner': {
    minWidth: 2048,
    minHeight: 1152,
    maxBytes: 6 * MB,
    contentTypes: ['image/jpeg', 'image/png'],
    aspect: 16 / 9,
  },
  'facebook.cover': {
    minWidth: 400,
    minHeight: 150,
    maxBytes: 10 * MB,
    contentTypes: ['image/jpeg', 'image/png'],
    aspect: null,
  },
  'facebook.picture': {
    minWidth: 180,
    minHeight: 180,
    maxBytes: 10 * MB,
    contentTypes: ['image/jpeg', 'image/png'],
    aspect: 1,
  },
  'deezer.picture': {
    minWidth: 500,
    minHeight: 500,
    maxBytes: 10 * MB,
    contentTypes: ['image/jpeg', 'image/png'],
    aspect: 1,
  },
};

export type ProfileSendFileRefusal = 'preview' | 'tooSmall' | 'tooLarge' | 'type' | 'shape';

/** The facts of a stored file that the rules read. */
export interface ProfileSendFileFacts {
  width: number;
  height: number;
  watermark: boolean;
  preview: boolean;
  mimeType: string | null;
  sizeBytes: number | null;
}

/**
 * Why the stored file of an image item cannot go to the service, or null. A preview (half size,
 * with the mark) never goes. Pure.
 */
export function profileSendFileRefusal(
  service: ProfileSendService,
  item: ProfileSendItem,
  file: ProfileSendFileFacts
): ProfileSendFileRefusal | null {
  if (file.preview || file.watermark) {
    return 'preview';
  }
  if (service === 'spotify' && (item === 'header' || item === 'picture')) {
    return spotifyProfileFileRefusal(item === 'header' ? 'header' : 'avatar', file);
  }
  const rule = PROFILE_SEND_FILE_RULES[`${service}.${item}`];
  if (rule === undefined) {
    return null;
  }
  if (file.mimeType !== null && !rule.contentTypes.includes(file.mimeType)) {
    return 'type';
  }
  if (file.width < rule.minWidth || file.height < rule.minHeight) {
    return 'tooSmall';
  }
  if (
    rule.aspect !== null &&
    file.height > 0 &&
    Math.abs(file.width / file.height - rule.aspect) > rule.aspect * 0.01
  ) {
    return 'shape';
  }
  if (file.sizeBytes !== null && file.sizeBytes > rule.maxBytes) {
    return 'tooLarge';
  }
  return null;
}

/** The YouTube channel description takes 1000 characters and no angle brackets. */
export const YOUTUBE_DESCRIPTION_MAX = 1000;
/** The Facebook Page "about" field takes 255 characters. */
export const FACEBOOK_ABOUT_MAX = 255;

/**
 * Cuts a text to at most `max` characters (code points) at the end of a sentence; with no sentence
 * end inside the limit, at the last space, with an ellipsis. Pure.
 */
export function cutAtSentence(text: string, max: number): string {
  const chars = Array.from(text.trim());
  if (chars.length <= max) {
    return chars.join('');
  }
  const head = chars.slice(0, max).join('');
  const sentenceEnd = Math.max(
    head.lastIndexOf('. '),
    head.lastIndexOf('! '),
    head.lastIndexOf('? '),
    head.lastIndexOf('\n')
  );
  if (sentenceEnd > max / 3) {
    return head.slice(0, sentenceEnd + 1).trim();
  }
  const room = chars.slice(0, max - 1).join('');
  const space = room.lastIndexOf(' ');
  return `${(space > 0 ? room.slice(0, space) : room).trim()}…`;
}

/**
 * The text that the service gets from the saved bio, or '' when there is nothing to send. Pure.
 * - Spotify, Deezer: the long bio (Spotify takes only the long bio, as "Upload bio to Spotify").
 *   Deezer falls back to the short bio.
 * - YouTube: the long bio (else the short), cut at a sentence to 1000, no `<` or `>`.
 * - Facebook "about": the short bio (else the long, cut to 255).
 */
export function bioTextFor(service: ProfileSendService, bio: { bioShort: string; bioLong: string }): string {
  const short = normalizeBioText(bio.bioShort);
  const long = normalizeBioText(bio.bioLong);
  switch (service) {
    case 'spotify':
      return bioLength(long) > ARTIST_BIO_LONG_MAX ? '' : long;
    case 'deezer':
      return long !== '' ? long : short;
    case 'youtube':
      return cutAtSentence((long !== '' ? long : short).replace(/[<>]/g, ''), YOUTUBE_DESCRIPTION_MAX);
    case 'facebook':
      return short !== '' ? cutAtSentence(short, FACEBOOK_ABOUT_MAX) : cutAtSentence(long, FACEBOOK_ABOUT_MAX);
    default:
      return '';
  }
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * The bio as the Deezer for Creators editor stores it: one `<p>` per paragraph, `<br>` for a line
 * break inside one, every other character escaped. Deezer allows only P, BR, STRONG, B, EM, I, U.
 * Pure.
 */
export function deezerBioHtml(text: string): string {
  return normalizeBioText(text)
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== '')
    .map((paragraph) => `<p>${paragraph.split('\n').map(escapeHtml).join('<br>')}</p>`)
    .join('');
}

// ---------------------------------------------------------------------------------------------
// The API answers
// ---------------------------------------------------------------------------------------------

/** One item of one service in the sheet: can it go now, and what went last. */
export interface ProfileSendItemAvailability {
  item: ProfileSendItem;
  /** Null: it can go. */
  unavailable: ProfileSendUnavailable | null;
  /** Only with `fileRefused`. */
  fileRefusal: ProfileSendFileRefusal | null;
  /** The profile version that would go (image items), or null. */
  assetUuid: string | null;
  /** The newest send of the item, or null. */
  last: { sendUuid: string; status: ProfileSendStatus; at: string; assetUuid: string | null } | null;
}

export interface ProfileSendServiceAvailability {
  service: ProfileSendService;
  /** True when the artist connected the service (it can still need a reconnect). */
  connected: boolean;
  /** The name of the connected profile (channel, page, artist), or null. */
  accountName: string | null;
  /** A reason for the whole service (every item has it too), or null. */
  unavailable: ProfileSendUnavailable | null;
  items: ProfileSendItemAvailability[];
}

export interface SerializedProfileSendItem {
  uuid: string;
  service: ProfileSendService;
  item: ProfileSendItem;
  status: ProfileSendStatus;
  unavailable: ProfileSendUnavailable | null;
  failCode: ProfileSendFailCode | null;
  /** The text of the service for a failed item, cut, with no URL and no secret. */
  failDetail: string | null;
  /** The profile version that went (image items). */
  assetUuid: string | null;
  /** The page of the profile on the service, when known. */
  externalUrl: string | null;
  finishedAt: string | null;
}

export interface SerializedProfileSend {
  uuid: string;
  createdAt: string;
  /** True: "Keep my profiles in sync" started it, not a click. */
  automatic: boolean;
  createdBy: { id: number; email: string } | null;
  rightsConfirmedAt: string;
  /** `working` while an item is queued or sending, else `done`. */
  status: 'working' | 'done';
  items: SerializedProfileSendItem[];
}

/** `GET /accounts/:accountId/artist-profile/send`. */
export interface ProfileSendOverviewResponse {
  services: ProfileSendServiceAvailability[];
  rights: { accepted: boolean; text: string; version: string };
  /** "Keep my profiles in sync": off by default. */
  keepInSync: { enabled: boolean; changedAt: string | null };
  /** The newest sends, newest first (at most 5). */
  recent: SerializedProfileSend[];
}

/** One service and its ticked items in `POST /accounts/:accountId/artist-profile/send`. */
export interface ProfileSendTarget {
  service: ProfileSendService;
  items: ProfileSendItem[];
}

/** `POST /accounts/:accountId/artist-profile/send` → 201, and `GET .../send/:sendUuid` → 200. */
export interface ProfileSendResponse {
  send: SerializedProfileSend;
}

/** `PUT /accounts/:accountId/artist-profile/send/settings` → 200. */
export interface ProfileSendSettingsResponse {
  keepInSync: { enabled: boolean; changedAt: string | null };
}

/** The error codes of the send routes (`cause.code`). */
export const PROFILE_SEND_ERROR_CODES = [
  'M24_PROFILE_SEND_INVALID',
  'M24_PROFILE_RIGHTS_REQUIRED',
  'M24_PROFILE_SEND_NOT_FOUND',
] as const;
export type ProfileSendErrorCode = (typeof PROFILE_SEND_ERROR_CODES)[number];
